/**
 * Settings Page
 *
 * Multi-tab settings form with premium card-based layout.
 *
 * @package PresszoneComments
 */

import API from '../utils/api.js';
import { el, clear, showLoading, __ } from '../utils/dom.js';
import Button, { SaveButton } from '../components/Button.js';
import { Tabs, TabPanel } from '../components/Tabs.js';
import { Card } from '../components/Card.js';
import { Toggle } from '../components/Toggle.js';
import { FormField } from '../components/FormField.js';
import { Select } from '../components/Select.js';
import { Textarea } from '../components/Textarea.js';
import { Placeholders } from '../components/Placeholders.js';
import { TagInput } from '../components/TagInput.js';
import Confirm from '../utils/confirm.js';

// State
let settings = {};
let originalSettings = {};
let activeTab = 'general';
let saving = false;
let isDirty = false;
let slideDirection = null; // Track animation direction for tab transitions
let mainContainer = null;
let mainAddNotice = null;

/**
 * Get tab definitions.
 */
function getTabs() {
    return [
        { id: 'general', label: __('General', 'comments-press-zone'), icon: '⚙️' },
        { id: 'emails', label: __('Emails', 'comments-press-zone'), icon: '📧' },
        { id: 'spam', label: __('Spam', 'comments-press-zone'), icon: '🛡️' },
        { id: 'status', label: __('Cache', 'comments-press-zone'), icon: '⚡' },
    ];
}

/**
 * Render the settings page.
 */
export async function renderSettings(container, addNotice, subPath = '') {
    mainContainer = container;
    mainAddNotice = addNotice;
    const tabs = getTabs();
    const prevTab = activeTab;

    // If subPath is provided, use it as activeTab
    // Extract tab name from subPath (remove query string if present)
    const tabName = subPath.split('?')[0];
    if (tabName && tabs.find(t => t.id === tabName)) {
        activeTab = tabName;
    } else if (!subPath) {
        activeTab = 'general';
    }

    // Optimization: If shell already rendered, handle partial update
    // Use a unique identifier to avoid matching Design page which also uses .presszone-comments-settings
    const hasShell = container.querySelector('.presszone-comments-settings[data-page="settings"]');
    if (hasShell) {
        // Update tab active states visually immediately
        container.querySelectorAll('.presszone-comments-tabs__item').forEach(tabEl => {
            const isActive = tabEl.dataset.tab === activeTab;
            tabEl.classList.toggle('presszone-comments-tabs__item--active', isActive);
            tabEl.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        if (prevTab !== activeTab) {
            const contentArea = container.querySelector('.presszone-comments-settings__content');
            if (contentArea) {
                // Clear and render with animation
                clear(contentArea);
                const panel = TabPanel({
                    content: renderTabContent(),
                    direction: slideDirection,
                    className: 'presszone-comments-settings__panel-wrapper'
                });
                contentArea.appendChild(panel);
                slideDirection = null; // Reset
            }
        }
        return;
    }

    showLoading(container, __('Loading settings...', 'comments-press-zone'));

    try {
        settings = await API.get('/settings');

        // Set defaults for new installations
        if (settings.max_nesting === undefined) settings.max_nesting = 5;
        if (settings.enable_like === undefined) settings.enable_like = true;
        if (settings.enable_dislike === undefined) settings.enable_dislike = true;
        if (settings.enable_share === undefined) settings.enable_share = true;
        if (settings.post_comment_notification === undefined) settings.post_comment_notification = true;
        if (settings.post_comment_tooltip_text === undefined) settings.post_comment_tooltip_text = 'Thank you for your comment! Share it on Social Media.';

        originalSettings = JSON.parse(JSON.stringify(settings)); // Deep clone
        isDirty = false;
        render();
    } catch (error) {
        clear(container);
        container.appendChild(
            el('div', { class: 'presszone-comments-error' },
                el('h2', {}, __('Error loading settings', 'comments-press-zone')),
                el('p', {}, error.message)
            )
        );
        addNotice?.(error.message, 'error');
    }
}

// ============ Component Wrappers (adapt shared components to local state) ============

/** Wrapper for Card component */
const renderSettingsCard = (icon, title, description, children, options) =>
    Card(icon, title, description, children, options);

/** Wrapper for Toggle - adapts (key, title, desc, isWarning) to shared API */
const renderToggleRow = (key, title, description, isWarning = false) =>
    Toggle(key, title, description, settings, (_k, _v) => { isDirty = true; }, isWarning);

/** Wrapper for Toggle that triggers re-render - for parent toggles with nested conditional settings */
const renderToggleRowWithRerender = (key, title, description, isWarning = false) =>
    Toggle(key, title, description, settings, (_k, _v) => {
        isDirty = true;
        render(); // Re-render to show/hide nested settings
    }, isWarning);

/** Wrapper for FormField - adapts local signature to shared API */
const renderSettingsField = (key, label, help, type = 'text', props = {}) =>
    FormField(key, label, help, type, settings, (_k, _v) => { isDirty = true; }, {
        styling: settings.styling,
        padding: settings.padding,
        showBorders: settings.show_borders,
        ...props
    });

/** Wrapper for Select - adapts local signature to shared API */
const renderSettingsSelect = (key, label, help, options, props = {}) =>
    Select(key, label, help, options, settings, (_k, _v) => { isDirty = true; }, null, {
        styling: settings.styling,
        padding: settings.padding,
        showBorders: settings.show_borders,
        ...props
    });

/** Wrapper for Textarea - adapts local signature to shared API */
const renderSettingsTextarea = (key, label, help, rows = 6) =>
    Textarea(key, label, help, settings, (_k, _v) => { isDirty = true; }, {
        rows,
        styling: settings.styling,
        padding: settings.padding,
        showBorders: settings.show_borders
    });

/**
 * Render the settings form.
 */
function render() {
    clear(mainContainer);

    mainContainer.appendChild(
        el('div', { class: 'presszone-comments-settings', 'data-page': 'settings' },
            // Header
            el('header', { class: 'presszone-comments-settings__header' },
                el('h1', {}, __('Settings', 'comments-press-zone')),
                SaveButton(
                    saving ? __('Saving...', 'comments-press-zone') : __('Save Settings', 'comments-press-zone'),
                    () => saveSettings(),
                    saving
                )
            ),

            // Tabs
            Tabs({
                tabs: getTabs(),
                activeTab,
                onChangeWithDirection: (id, direction) => {
                    slideDirection = direction;
                    window.location.hash = `#/settings/${id}`;
                    // handleRoute in admin.js will catch hashchange and re-render
                },
                onChange: () => { } // Handled by onChangeWithDirection
            }),

            // Content with slide animation
            el('div', { class: 'presszone-comments-settings__content' },
                TabPanel({
                    content: renderTabContent(),
                    direction: slideDirection,
                    className: 'presszone-comments-settings__panel-wrapper'
                })
            )
        )
    );

    // Reset direction after render (only animate on tab clicks, not on page load)
    slideDirection = null;
}

/**
 * Render current tab content.
 */
function renderTabContent() {
    switch (activeTab) {
        case 'general': return renderGeneralTab();
        case 'emails': return renderEmailsTab();
        case 'spam': return renderSpamTab();
        case 'status': return renderStatusTab();
        default: return el('div', {}, __('Unknown tab', 'comments-press-zone'));
    }
}

/**
 * General Settings Tab - Premium Card Layout
 */
function renderGeneralTab() {
    return el('div', { class: 'presszone-comments-settings__panel' },
        // Comments Display Card
        renderSettingsCard('📄', __('Comments Display', 'comments-press-zone'),
            __('How comments appear to your users', 'comments-press-zone'),
            [
                el('div', { style: 'display: flex; gap: 24px; flex-wrap: wrap; justify-content: space-between;' },
                    (() => {
                        const f = renderSettingsSelect('display_order', __('Display Order', 'comments-press-zone'),
                            __('Order in which comments are displayed', 'comments-press-zone'),
                            [
                                { value: 'newest', label: __('Newest First', 'comments-press-zone'), default: true },
                                { value: 'oldest', label: __('Oldest First', 'comments-press-zone') }
                            ], { style: { maxWidth: '160px' } });
                        f.style.minWidth = 'max-content';
                        return f;
                    })(),
                    (() => {
                        const f = renderSettingsField('max_nesting', __('Max Nesting', 'comments-press-zone'),
                            __('Maximum reply nesting depth (1-5)', 'comments-press-zone'), 'number',
                            { min: 1, max: 5, style: { maxWidth: '120px' } });
                        return f;
                    })(),
                    (() => {
                        const f = renderSettingsSelect('comments_per_page', __('Comments per Page', 'comments-press-zone'),
                            __('Pagination setting', 'comments-press-zone'),
                            [
                                { value: '10', label: '10' },
                                { value: '20', label: '20', default: true },
                                { value: '50', label: '50' }
                            ], { style: { maxWidth: '120px' } });
                        
                        // Add class for mobile responsive styling
                        f.classList.add('presszone-comments-settings-compact-mobile');
                        f.style.minWidth = 'max-content';

                        return f;
                    })()
                )
            ], { staggerIndex: 0 }
        ),

        // Engagement Card
        renderSettingsCard('👍', __('Engagement', 'comments-press-zone'),
            __('User interaction options', 'comments-press-zone'),
            [
                renderToggleRow('enable_like', __('Enable Like', 'comments-press-zone'),
                    __('Allow users to like comments', 'comments-press-zone')),
                renderToggleRow('enable_dislike', __('Enable Dislike', 'comments-press-zone'),
                    __('Allow users to dislike comments', 'comments-press-zone'))
            ], { staggerIndex: 1 }
        ),

        // Sharing Card
        renderSettingsCard('📤', __('Sharing', 'comments-press-zone'),
            __('Social sharing options', 'comments-press-zone'),
            [
                renderToggleRowWithRerender('enable_share', __('Enable "Share"', 'comments-press-zone'),
                    __('Allow users to share comments on social media', 'comments-press-zone')),
                settings.enable_share ? el('div', { class: 'presszone-comments-settings-nested' },
                    renderToggleRow('enable_share_facebook', __('Facebook', 'comments-press-zone'),
                        __('Allow sharing on Facebook', 'comments-press-zone')),
                    renderToggleRow('enable_share_twitter', __('X/Twitter', 'comments-press-zone'),
                        __('Allow sharing on X/Twitter', 'comments-press-zone')),
                    renderToggleRow('enable_share_linkedin', __('LinkedIn', 'comments-press-zone'),
                        __('Allow sharing on LinkedIn', 'comments-press-zone'))
                ) : null
            ], { staggerIndex: 2 }
        ),

        // Comment Notification Card
        renderSettingsCard('🎉', __('Comment Notification', 'comments-press-zone'),
            __('Celebrate user engagement', 'comments-press-zone'),
            [
                renderToggleRowWithRerender('post_comment_notification', __('Comment notification', 'comments-press-zone'),
                    __('Show notification after posting a comment', 'comments-press-zone')),
                settings.post_comment_notification ? el('div', { class: 'presszone-comments-settings-nested' },
                    renderToggleRow('post_comment_confetti', __('Confetti', 'comments-press-zone'),
                        __('Show confetti animation after posting', 'comments-press-zone')),
                    renderSettingsTextarea('post_comment_tooltip_text', __('Tooltip Text', 'comments-press-zone'),
                        __('Message shown after posting a comment. Use {post_comment_text} as placeholder for custom text.', 'comments-press-zone'), 2)
                ) : null
            ], { staggerIndex: 3 }
        )
    );
}


/**
 * Emails Settings Tab - Premium Card Layout
 */
function renderEmailsTab() {
    return el('div', { class: 'presszone-comments-settings__panel' },
        // Placeholders Reference Card
        renderSettingsCard('📝', __('Available Placeholders', 'comments-press-zone'),
            __('Use these placeholders in your email templates', 'comments-press-zone'),
            [
                Placeholders({
                    items: [
                        { tag: '{username}', desc: __('Recipient name', 'comments-press-zone') },
                        { tag: '{site_name}', desc: __('Site name', 'comments-press-zone') },
                        { tag: '{comments_url}', desc: __('Comments URL', 'comments-press-zone') },
                        { tag: '{date}', desc: __('Current date', 'comments-press-zone') },
                        { tag: '{reason}', desc: __('Ban reason (bans only)', 'comments-press-zone') },
                        { tag: '{expires}', desc: __('Ban expiry (bans only)', 'comments-press-zone') },
                        { tag: '{digest_content}', desc: __('Activity summary (digest only)', 'comments-press-zone') }
                    ],
                    type: 'grid',
                    addNotice: mainAddNotice
                })
            ], { collapsible: true, collapsed: false, staggerIndex: 0 }
        ),

        // Daily Digest Card
        renderSettingsCard('📊', __('Daily Digest Email', 'comments-press-zone'),
            __('Sent daily to users who opt-in via their account preferences', 'comments-press-zone'),
            [
                renderSettingsField('digest_email_subject', __('Subject', 'comments-press-zone'),
                    null, 'text'),
                renderSettingsTextarea('digest_email_body', __('Body', 'comments-press-zone'),
                    null, 8)
            ], { staggerIndex: 1 }
        ),

        // Ban Notification Card
        renderSettingsCard('🚫', __('Ban Notification Email', 'comments-press-zone'),
            __('Sent automatically when a user is banned', 'comments-press-zone'),
            [
                renderSettingsField('banned_email_subject', __('Subject', 'comments-press-zone'),
                    null, 'text'),
                renderSettingsTextarea('banned_email_body', __('Body', 'comments-press-zone'),
                    null, 8)
            ], { staggerIndex: 2 }
        ),

        // Subscription Notification Card
        renderSettingsCard('🔔', __('Subscription Notification Email', 'comments-press-zone'),
            __('Sent when a new reply is posted in a watched comment thread', 'comments-press-zone'),
            [
                el('div', { class: 'presszone-comments-settings-card__info', style: 'margin-bottom: 16px;' },
                    Placeholders({
                        items: ['{thread_title}', '{thread_url}', '{author_name}'],
                        type: 'inline',
                        label: __('Additional placeholders:', 'comments-press-zone'),
                        addNotice: mainAddNotice
                    })
                ),
                renderSettingsField('subscription_email_subject', __('Subject', 'comments-press-zone'),
                    null, 'text'),
                renderSettingsTextarea('subscription_email_body', __('Body', 'comments-press-zone'),
                    null, 8)
            ], { staggerIndex: 3 }
        )
    );
}

/**
 * Spam Settings Tab - Premium Card Layout
 */
function renderSpamTab() {
    return el('div', { class: 'presszone-comments-settings__panel' },
        // Content Restrictions Card
        renderSettingsCard('🔗', __('Content Restrictions', 'comments-press-zone'),
            __('Control what content is allowed in comments', 'comments-press-zone'),
            [
                renderToggleRow('allow_external_links', __('Allow External Links', 'comments-press-zone'),
                    __('Permit users to include external links in comments', 'comments-press-zone'))
            ], { staggerIndex: 0 }
        ),

        // Anti-Spam Card (Banned Words)
        renderSettingsCard('🚫', __('Banned Words', 'comments-press-zone'),
            __('Words that will be automatically filtered from comments', 'comments-press-zone'),
            [
                // Help text explaining wildcard syntax
                el('div', { class: 'presszone-comments-settings-card__info', style: 'margin-bottom: 16px;' },
                    el('p', { style: 'margin: 0 0 8px 0; font-size: 13px; color: var(--presszone-comments-text-secondary);' },
                        __('Use asterisk (*) for wildcards. Without wildcards, matches complete words only.', 'comments-press-zone')
                    ),
                    el('div', { style: 'display: flex; gap: 16px; flex-wrap: wrap; font-size: 12px;' },
                        el('div', {},
                            el('strong', {}, __('Examples:', 'comments-press-zone'))
                        ),
                        el('div', {},
                            el('code', { style: 'background: var(--presszone-comments-bg-secondary); padding: 2px 6px; border-radius: 3px;' }, 'ass'),
                            el('span', { style: 'margin-inline-start: 4px; color: var(--presszone-comments-text-secondary);' }, __('→ blocks "ass" only', 'comments-press-zone'))
                        ),
                        el('div', {},
                            el('code', { style: 'background: var(--presszone-comments-bg-secondary); padding: 2px 6px; border-radius: 3px;' }, '*ass*'),
                            el('span', { style: 'margin-inline-start: 4px; color: var(--presszone-comments-text-secondary);' }, __('→ blocks "class", "badass", etc.', 'comments-press-zone'))
                        ),
                        el('div', {},
                            el('code', { style: 'background: var(--presszone-comments-bg-secondary); padding: 2px 6px; border-radius: 3px;' }, 'f*ck'),
                            el('span', { style: 'margin-inline-start: 4px; color: var(--presszone-comments-text-secondary);' }, __('→ blocks "fuck", "fack", "f123ck"', 'comments-press-zone'))
                        )
                    )
                ),
                // Tag input field (updated placeholder)
                TagInput('banned_words', __('Add banned words', 'comments-press-zone'),
                    __('Type a word and press Enter to add (supports * wildcards)', 'comments-press-zone'),
                    settings, (_k, _v) => { isDirty = true; })
            ], { staggerIndex: 1 }
        ),

        // Reply Throttling Card
        renderSettingsCard('⏱️', __('Reply Throttling', 'comments-press-zone'),
            __('Limit how often users can post comments', 'comments-press-zone'),
            [
                renderToggleRowWithRerender('enable_throttling', __('Reply throttling', 'comments-press-zone'),
                    __('Limit comment frequency per user', 'comments-press-zone')),
                settings.enable_throttling ? el('div', { class: 'presszone-comments-settings-nested' },
                    el('div', { style: 'display: flex; gap: 12px; align-items: center; flex-wrap: wrap;' },
                        el('span', {}, __('Limit to', 'comments-press-zone')),
                        el('input', {
                            type: 'number',
                            id: 'throttle_limit',
                            class: 'presszone-comments-input--settings',
                            style: { width: '70px' },
                            value: settings.throttle_limit || 5,
                            min: 1,
                            max: 100,
                            onchange: (e) => {
                                settings.throttle_limit = parseInt(e.target.value, 10);
                                isDirty = true;
                            }
                        }),
                        el('span', {}, __('replies in', 'comments-press-zone')),
                        el('input', {
                            type: 'number',
                            id: 'throttle_seconds',
                            class: 'presszone-comments-input--settings',
                            style: { width: '80px' },
                            value: settings.throttle_seconds || 60,
                            min: 10,
                            max: 86400,
                            onchange: (e) => {
                                settings.throttle_seconds = parseInt(e.target.value, 10);
                                isDirty = true;
                            }
                        }),
                        el('span', {}, __('seconds', 'comments-press-zone'))
                    )
                ) : null
            ], { staggerIndex: 2 }
        ),

        // reCAPTCHA Card (existing)
        renderSettingsCard('🤖', __('reCAPTCHA v3 Integration', 'comments-press-zone'),
            __('Automatically block bots using invisible score-based detection', 'comments-press-zone'),
            [
                renderToggleRowWithRerender('enable_recaptcha', __('Enable reCAPTCHA v3', 'comments-press-zone'),
                    __('Protect forms with Google reCAPTCHA', 'comments-press-zone')),
                settings.enable_recaptcha ? el('div', { class: 'presszone-comments-settings-nested' },
                    renderSettingsField('recaptcha_site_key', __('Site Key', 'comments-press-zone'),
                        __('Your Google reCAPTCHA v3 Site Key', 'comments-press-zone'), 'text'),
                    renderSettingsField('recaptcha_secret_key', __('Secret Key', 'comments-press-zone'),
                        __('Your Google reCAPTCHA v3 Secret Key', 'comments-press-zone'), 'password')
                ) : null
            ], { staggerIndex: 3 }
        )
    );
}

/**
 * Status & Caching Settings Tab - Premium Card Layout
 */
function renderStatusTab() {
    const backendLabels = { redis: __('Redis', 'comments-press-zone'), memcached: __('Memcached', 'comments-press-zone'), wp: __('WordPress Object Cache', 'comments-press-zone') };
    const redisAvailable = settings.redis_available;
    const memcachedAvailable = settings.memcached_available;

    return el('div', { class: 'presszone-comments-settings__panel' },
        // Server Info Card
        renderSettingsCard('⚡', __('Server Information', 'comments-press-zone'),
            __('Current system status', 'comments-press-zone'),
            [
                el('div', { class: 'presszone-comments-info-grid--card' },
                    el('div', { class: 'presszone-comments-info-item--card' },
                        el('span', { class: 'presszone-comments-info-label--card' }, __('Plugin Version', 'comments-press-zone')),
                        el('span', { class: 'presszone-comments-info-value--card' }, settings.version || '-')
                    ),
                    el('div', { class: 'presszone-comments-info-item--card' },
                        el('span', { class: 'presszone-comments-info-label--card' }, __('Active Cache Backend', 'comments-press-zone')),
                        el('span', { class: `presszone-comments-badge presszone-comments-badge--${settings.cache_backend || 'wp'}` },
                            backendLabels[settings.cache_backend] || __('WordPress Object Cache', 'comments-press-zone'))
                    )
                )
            ], { staggerIndex: 0 }
        ),

        // Redis Card
        renderSettingsCard(redisAvailable ? '🟢' : '🔴', __('Redis Support', 'comments-press-zone'),
            redisAvailable
                ? __('High-performance caching available', 'comments-press-zone')
                : __('Redis PHP extension not installed', 'comments-press-zone'),
            redisAvailable ? [
                renderToggleRowWithRerender('enable_redis', __('Activate Redis Support', 'comments-press-zone'),
                    __('Enable high-performance object caching', 'comments-press-zone')),
                settings.enable_redis ? el('div', { class: 'presszone-comments-settings-subset' },
                    renderSettingsField('redis_host', __('Host', 'comments-press-zone'),
                        null, 'text'),
                    renderSettingsField('redis_port', __('Port', 'comments-press-zone'),
                        null, 'number', { min: 1, max: 65535, style: { maxWidth: '120px' } }),
                    el('div', { class: 'presszone-comments-form-group' },
                        el('label', { class: 'presszone-comments-label--settings', for: 'redis_password' },
                            __('Password', 'comments-press-zone')
                        ),
                        el('input', {
                            type: 'password',
                            id: 'redis_password',
                            class: 'presszone-comments-input--settings',
                            value: settings.redis_password || '',
                            onchange: (e) => updateSetting('redis_password', e.target.value)
                        })
                    ),
                    renderSettingsField('redis_database', __('Database Index', 'comments-press-zone'),
                        null, 'number', { min: 0, max: 15, style: { maxWidth: '120px' } })
                ) : null
            ] : [
                el('div', { class: 'presszone-comments-unavailable-notice' },
                    el('div', { class: 'presszone-comments-unavailable-notice__steps' },
                        el('strong', {}, __('How to enable:', 'comments-press-zone')),
                        el('ol', {},
                            el('li', {}, __('Install Redis server on your system', 'comments-press-zone')),
                            el('li', {}, __('Install the PHP Redis extension (e.g., pecl install redis)', 'comments-press-zone')),
                            el('li', {}, __('Add extension=redis.so to your php.ini', 'comments-press-zone')),
                            el('li', {}, __('Restart your web server', 'comments-press-zone'))
                        )
                    )
                )
            ], { staggerIndex: 1 }
        ),

        // Memcached Card
        renderSettingsCard(memcachedAvailable ? '🟢' : '🔴', __('Memcached Support', 'comments-press-zone'),
            memcachedAvailable
                ? __('Fallback caching available', 'comments-press-zone')
                : __('Memcached PHP extension not installed', 'comments-press-zone'),
            memcachedAvailable ? [
                renderToggleRowWithRerender('enable_memcached', __('Activate Memcached Support', 'comments-press-zone'),
                    __('Enable fallback object caching if Redis is not used', 'comments-press-zone')),
                settings.enable_memcached ? el('div', { class: 'presszone-comments-settings-subset' },
                    renderSettingsField('memcached_host', __('Host', 'comments-press-zone'),
                        null, 'text'),
                    renderSettingsField('memcached_port', __('Port', 'comments-press-zone'),
                        null, 'number', { min: 1, max: 65535, style: { maxWidth: '120px' } })
                ) : null
            ] : [
                el('div', { class: 'presszone-comments-unavailable-notice' },
                    el('div', { class: 'presszone-comments-unavailable-notice__steps' },
                        el('strong', {}, __('How to enable:', 'comments-press-zone')),
                        el('ol', {},
                            el('li', {}, __('Install Memcached server on your system', 'comments-press-zone')),
                            el('li', {}, __('Install the PHP Memcached extension (e.g., pecl install memcached)', 'comments-press-zone')),
                            el('li', {}, __('Add extension=memcached.so to your php.ini', 'comments-press-zone')),
                            el('li', {}, __('Restart your web server', 'comments-press-zone'))
                        )
                    )
                )
            ], { staggerIndex: 2 }
        )
    );
}


// ============ Actions ============

function updateSetting(key, value) {
    settings[key] = value;
    isDirty = true;
}

async function saveSettings() {
    saving = true;
    render();

    try {
        await API.post('/settings', settings);
        originalSettings = JSON.parse(JSON.stringify(settings)); // Update original after save
        isDirty = false;
        mainAddNotice?.(__('Settings saved', 'comments-press-zone'), 'success');
    } catch (error) {
        mainAddNotice?.(error.message, 'error');
    } finally {
        saving = false;
        render();
    }
}

/**
 * Check if there are unsaved changes.
 */
export function hasUnsavedChanges() {
    return isDirty;
}

/**
 * Show unsaved changes warning modal.
 * Returns a Promise that resolves to 'save', 'discard', or 'cancel'.
 */
export function showUnsavedWarning() {
    return new Promise((resolve) => {
        const overlay = el('div', { class: 'presszone-comments-modal-overlay' },
            el('div', { class: 'presszone-comments-modal', style: 'max-width: 400px;' },
                el('div', { class: 'presszone-comments-modal__header' },
                    el('h2', {}, __('Unsaved Changes', 'comments-press-zone')),
                    el('button', {
                        class: 'presszone-comments-modal__close',
                        onclick: () => { overlay.remove(); resolve('cancel'); }
                    }, 'x')
                ),
                el('div', { class: 'presszone-comments-modal__form' },
                    el('p', {}, __('You have unsaved changes. What would you like to do?', 'comments-press-zone')),
                    el('div', { class: 'presszone-comments-modal__footer' },
                        Button({
                            label: __('Discard', 'comments-press-zone'),
                            variant: 'secondary',
                            onClick: () => {
                                settings = JSON.parse(JSON.stringify(originalSettings)); // Revert
                                isDirty = false;
                                overlay.remove();
                                resolve('discard');
                            }
                        }),
                        Button({
                            label: __('Save', 'comments-press-zone'),
                            onClick: async () => {
                                overlay.remove();
                                await saveSettings();
                                resolve('save');
                            }
                        })
                    )
                )
            )
        );
        document.body.appendChild(overlay);
    });
}

export default { renderSettings };
